/**
 *
 * @module scores
 * @author Adam Pirani [greg.babula@mlb.com]
 * @description MLB Scores viewModel
 *
 */

import G5ViewModel from 'g5-component/src/scripts/viewModel/master';
import util from 'util';
import cloneDeep from 'lodash-es/cloneDeep';
import assign from 'lodash-es/assign';
import create from 'lodash-es/create';
import get from 'lodash-es/get';
import fedComponentMlbScores from 'fed-component-mlb-scores';
import EventEmitter from 'events';
import templates from '../component/templates';
import partials from '../component/partials';
import scoresControls from './extender/controls';
import scoresExtender from '../component/extender';
import MODES_USING_SPRITE from '../../json/modes-using-sprite.json';
import ViewModelHelpers from '../helpers/viewModelHelpers';

const { postseasonPartial, controlsPartial, gamesPartial, minisbHeaderPartial, minisbLogosPartial, spinnerPartial } = partials;
const { migTemplate, minisbTemplate, gameSelectorTemplate, responsiveTemplate, postseasonTemplate } = templates;
const SITE_IMAGE_URI = 'https://prod-gameday.mlbstatic.com/responsive-gameday-assets';

/**
 *
 * @name scoresViewModel
 *
 */
class ScoresViewModel extends G5ViewModel {

    constructor(_opts, impl) {

        /**
         *
         * @name opts
         * @description scores viewModel default options
         *
         */

        const opts = assign({
            container: document.querySelector('.mlb-scores')
        }, _opts);

        /**
         *
         * @name super
         * @description scores model default options
         *
         */
        super(_opts, impl);

        /**
         *
         * @name core
         * @description scores viewModel core properties
         *
         */
        this.core = {
            controls: {}
        };

        /**
         *
         * @name props
         * @description scores viewModel properties
         *
         */
        this.props = {
            instance: false,
            hasComponents: false,
            element: null,
            autoPlayGamePk: null
        };

        /**
         *
         * @name reference
         * @description Object reference store
         *
         */
        this.reference = {
            components: {},
            pspComponents: {},
            pausedComponents: {},
            componentsPlayingVideo: {}
        };

    }

    /**
     *
     * @method gameSelectorEvent
     * @param {Object} gamePk
     * @description emits game selection event
     *
    */
    gameSelectorEvent(gamePk) {

        this.emit('game-selected', gamePk);

    }

    /**
     *
     * @method spinner
     * @description toggles spinner for game-selector and responsive SB
     *
    */
    spinner() {

        const spinners = document.querySelectorAll('.p-loading');

        if (spinners[0]) {
            spinners[0].classList.toggle('mlb-scores--hidden');
        }

        if (spinners[1]) {
            spinners[1].classList.toggle('mlb-scores--hidden');
        }

    }

    /**
     *
     * @method createScoresComponent
     * @param {Object} data
     * @param {Object} opts
     * @returns {Object} scoresComponent
     * @description creates a scores component instance
     *
     */
    createScoresComponent(data = {}, isCurrentGamePostseasonPush = false) {

        const { opts, props } = this;
        const { gamePk, gameType, tiebreaker, nonGameEvent = false } = data;
        const { lang, classes, mlbMoment, mlbLinker, gameUtils, videoEmbedder, isPostseasonPush, milbSportIds, showDescription = '', defaultPromo } = opts;
        const { teamId, sportId, leagueId, device, siteAssetsUri, liveLookInSponsor, homeRunDerbyUrl, homeRunDerbyImgSrc, youtubeMLBLink, isPartner = false } = opts;
        const { siteImageUri = SITE_IMAGE_URI } = opts;
        const { element, autoPlayGamePk } = props;
        const { getGameKey } = ViewModelHelpers;
        let { mode } = opts;
        const gameKey = getGameKey(mode, data);
        const useSprite = !!MODES_USING_SPRITE[mode];
        const container = isCurrentGamePostseasonPush
            ? element.querySelector(`.g5-component--mlb-scores--postseason-push-${gamePk}`)
            : element.querySelector(`.g5-component--mlb-scores--${gameKey}`);

        if (mode === 'game-selector') {

            container.addEventListener('click', this.gameSelectorEvent.bind(this, gamePk));

        } else if (mode === 'postseason') {

            classes.push('g5-component--mlb-scores--postseason');
            mode = 'MIG';

        }

        const config = {
            component: 'g5-component--mlb-scores',
            css: `${classes.join(' ')}`,
            lang,
            mode,
            gamePk,
            container,
            enableFetch: false,
            enablePolling: false,
            mlbMoment,
            mlbLinker,
            defaultPromo,
            gameUtils,
            homeRunDerbyUrl,
            homeRunDerbyImgSrc,
            youtubeMLBLink,
            milbSportIds,
            videoEmbedder,
            nonGameEvent,
            autoPlay: autoPlayGamePk === gamePk,
            teamId,
            sportId,
            leagueId,
            device,
            siteAssetsUri,
            siteImageUri,
            useSprite,
            liveLookInSponsor,
            isPostseasonPush,
            imgContext: isPartner ? 'league' : 'sport'
        };

        if (mode === 'MIG') {

            config.showDescription = showDescription;

        }

        const scoresComponent = fedComponentMlbScores(config);

        return scoresComponent;

    }

    /**
     *
     * @method bind
     * @param {Object} data
     * @description binds data to template
     *
     */
    bind(data = {}) {
        const self = this;
        const { opts, props, reference } = this;
        const { pausedComponents, componentsPlayingVideo } = reference;
        let { element, hasComponents } = props;
        const { mode, mlbMoment, teamId, adFrequency, device, LazyLoadMIG, sport, isPostseasonPush, show78x30Ads = true } = opts;
        const extendedData = scoresExtender(data, opts);
        const { groupGamesByTypes, sortGames, sortByGameStatus, setAds, getGameKey } = ViewModelHelpers;
        const elementHideModifier = 'mlb-scores--hidden';
        let numGames = 0;

        //
        // update property reference
        //
        props.hasComponents = true;

        //
        // bind data to the template a single time
        // the template only needs to be aware of the inital set so that
        // it can prepare the correct amount of elements
        //
        if (!hasComponents) {

            if (mode !== 'postseason') {

                this.Handlebars.registerPartial('controls', controlsPartial);
                this.Handlebars.registerPartial('games', gamesPartial);

            }

            if (mode === 'postseason') {

                extendedData.postSeasonGames = groupGamesByTypes(extendedData, opts);

                this.Handlebars.registerPartial('postseasonScoreboards', postseasonPartial);

            } else if (mode === 'minisb') {

                extendedData.isPostseasonPush = isPostseasonPush;
                this.Handlebars.registerPartial('minisbHeader', minisbHeaderPartial);
                this.Handlebars.registerPartial('minisbLogos', minisbLogosPartial);
                sortByGameStatus(extendedData, teamId, opts);

            } else if (mode === 'responsive') {

                sortByGameStatus(extendedData, teamId, opts);

            } else if (mode !== 'MIG') {

                sortGames(mlbMoment, extendedData, teamId, opts);

            }

            this.props.autoPlayGamePk = get(extendedData, 'dates[0].games[0].gamePk');

            if (adFrequency) {

                setAds(extendedData, adFrequency);

            }

            if (mode === 'postseason') {

                element.innerHTML = postseasonTemplate(extendedData);

            } else if (mode === 'MIG') {

                this.Handlebars.registerPartial('spinner', spinnerPartial);
                element.innerHTML = migTemplate(extendedData);

            } else if (mode === 'minisb') {

                element.innerHTML = minisbTemplate(extendedData);

            } else if (mode === 'responsive') {

                if (this.Handlebars.partials && !this.Handlebars.partials.spinner) {
                    this.Handlebars.registerPartial('spinner', spinnerPartial);
                }
                element.innerHTML = responsiveTemplate(extendedData);

            } else if (mode === 'game-selector') {

                this.Handlebars.registerPartial('spinner', spinnerPartial);
                sortByGameStatus(extendedData, teamId, opts);
                element.innerHTML = gameSelectorTemplate(extendedData);

            }

            element.classList.add('mlb-scores');
            element.classList.add(`mlb-scores--${mode}`);
            element.classList.add(`mlb-scores--${device}`);
            element.classList.add(`mlb-scores--${sport}`);

        }

        //
        // iterate over all games
        // store reference to component instance and pass in data via synthetic-data event
        if (mode === 'postseason') {

            if (!extendedData.postSeasonGames) {
                extendedData.postSeasonGames = groupGamesByTypes(extendedData, opts);
            }

            const { postSeasonGames } = extendedData;

            postSeasonGames.forEach((gameType) => {

                const gameTypeGames = gameType.games;

                gameTypeGames.forEach((game) => {

                    const gameKey = getGameKey(mode, game);
                    const isPaused = pausedComponents[gameKey] || componentsPlayingVideo[gameKey];
                    numGames = numGames + 1;

                    if (!hasComponents) {

                        reference.components[gameKey] = this.createScoresComponent(game);
                        reference.components[gameKey].init();

                    }
                    if (reference.components[gameKey] && reference.components[gameKey].hasInstance() && !isPaused) {

                        reference.components[gameKey].emit('synthetic-data', game);

                    }

                });

            });

        } else {

            if (hasComponents && isPostseasonPush) {
                sortByGameStatus(extendedData, teamId, opts);
            }

            const { dates } = extendedData;

            if (LazyLoadMIG) {
                LazyLoadMIG.allDates = [];
            }

            dates.forEach((set) => {

                const { games, events } = set;

                if (LazyLoadMIG) {
                    LazyLoadMIG.allDates.push(set.date);
                }

                if (isPostseasonPush) {
                    const { postseasonPushGames } = set;
                    postseasonPushGames.forEach((pspGame) => {
                        const gameKey = getGameKey(mode, pspGame);
                        const isPaused = pausedComponents[gameKey] || componentsPlayingVideo[gameKey];
                        numGames = numGames + 1;

                        if (!hasComponents) {

                            reference.pspComponents[gameKey] = this.createScoresComponent(pspGame, true);
                            reference.pspComponents[gameKey].init();

                        }
                        if (reference.pspComponents[gameKey] && reference.pspComponents[gameKey].hasInstance() && !isPaused) {

                            reference.pspComponents[gameKey].emit('synthetic-data', pspGame);

                        }
                    });
                }

                games.forEach((game) => {

                    const gameKey = getGameKey(mode, game);

                    const isPaused = pausedComponents[gameKey] || componentsPlayingVideo[gameKey];
                    numGames = numGames + 1;

                    if (!hasComponents) {

                        reference.components[gameKey] = this.createScoresComponent(game);
                        reference.components[gameKey].init();
                        reference.components[gameKey].viewModel.on('pause', this.pauseComponentRendering.bind(this));
                        reference.components[gameKey].viewModel.on('resume', this.resumeComponentRendering.bind(this));

                    }

                    if (reference.components[gameKey] && reference.components[gameKey].hasInstance() && !isPaused) {

                        reference.components[gameKey].emit('synthetic-data', game);

                        const isReadyToRefrehAds = mode === 'responsive' && game.status.abstractGameState === 'Live' && (game.linescore && game.linescore.currentInning > 9);
                        const adContainer = reference.components[gameKey].opts.container;

                        if (show78x30Ads && isReadyToRefrehAds && adContainer) {
                            self.emit('refresh-ads', adContainer);
                        }

                    }

                });

                if (mode === 'responsive' && sport === 'mlb') {

                    events.forEach((event) => {

                        const gameKey = event.id;

                        event.gamePk = gameKey;
                        event.nonGameEvent = true;
                        event.homeRunDerby = event.name.indexOf('All-Star Workout Day: Home Run Derby') > -1;

                        if (event.homeRunDerby) {
                            numGames = numGames + 1;

                            if (!hasComponents && event.nonGameEvent) {

                                reference.components[gameKey] = this.createScoresComponent(event);
                                reference.components[gameKey].init();

                            }

                            if (event.nonGameEvent && reference.components[gameKey] && reference.components[gameKey].hasInstance()) {

                                reference.components[gameKey].emit('synthetic-data', event);

                            }
                        }
                    });

                }

            });
        }

        //
        // If no games, hide element
        //
        if (numGames === 0) {

            element.classList.add(elementHideModifier);

        } else {

            element.classList.remove(elementHideModifier);

        }

        // if there are postseason games returned, add class
        if (data.hasPostseasonGame) {
            element.classList.add('mlb-scores--has-postseason');
        }

        // controls can bind without state check.
        this.bindControls(extendedData.selectedGameIdx);

        //
        // broadcast event
        //
        if (!hasComponents) {

            this.emit('games-ready', reference);

        }

    }

    /**
     *
     * @function bindControls
     * @description binds horizontal controls
     * @param selectedGameIdx Default game index selected (used in MIG)
     *
     */
    bindControls(selectedGameIdx = 0) {

        const { opts, core, props } = this;
        const { controls } = core;
        const { useControls } = ViewModelHelpers;

        if (useControls(opts)) {

            controls.init(selectedGameIdx);

        }

    }

    /**
     *
     * @function scrollToGame
     * @param {String} gamePk
     *
     */
    scrollToGame(gamePk) {

        const { opts, core } = this;
        const { controls } = core;
        const { useControls } = ViewModelHelpers;

        if (useControls(opts)) {

            controls.scrollToGame(gamePk);

        } else {

            const { props } = this;
            const { element } = props;

            const gameElement = element.querySelector(`.g5-component--mlb-scores--${gamePk}`);

            const then = Date.now();
            const duration = 400; // milliseconds

            (function step() {

                const target = gameElement.getBoundingClientRect().top + window.pageYOffset;
                const now = Date.now();

                /** @type {number} starting at 0, going to 1 */
                const completion = Math.min((now - then) / duration, 1);
                /** @type {number} starting at 1, going to 0 */
                const stabilization = Math.sin((Math.PI / 2) * (1 - completion));

                const targetY = (window.pageYOffset * stabilization) + (target * (1 - stabilization));
                window.scrollTo(0, targetY);

                if (completion < 1) {
                    requestAnimationFrame(step);
                }

            }());
        }
    }

    /**
     *
     * @function updateModifiers
     * @description updates default classes/modifiers on the container
     *
     */
    updateModifiers() {

        const { props } = this;
        const { element } = props;

        const classList = element.classList;
        const horizontalHideModifier = 'mlb-scores--horizontal--hide';
        const responsiveHideModifier = 'mlb-scores--responsive--hide';

        if (classList.contains(horizontalHideModifier)) {
            classList.remove(horizontalHideModifier);
        }

        if (classList.contains(responsiveHideModifier)) {
            classList.remove(responsiveHideModifier);
        }

    }

    /**
     * @function pauseComponentRendering
     * @param data object passed from component pause event
     * @description adds the gamePk for a component to the paused list
     */
    pauseComponentRendering(data) {

        const gamePk = data.gamePk;
        this.reference.pausedComponents[gamePk] = true;

    }

    /**
     * @function resumeComponentRendering
     * @param data object passed from component pause event
     * @description adds the gamePk for a component to the paused list
     */
    resumeComponentRendering(data) {

        const gamePk = data.gamePk;
        delete this.reference.pausedComponents[gamePk];

    }

    /**
     * @function videoCompleted
     * @description Video completed playing, can resume data updates
     * @param gamePk
     */
    videoCompleted(gamePk) {

        delete this.reference.componentsPlayingVideo[gamePk];
    }

    /**
     * @function videoPaused
     * @description Video is paused, can resume data updates
     * @param gamePk
     */
    videoPaused(gamePk) {

        delete this.reference.componentsPlayingVideo[gamePk];

    }

    /**
     * @function videoClosed
     * @description Video is closed, can resume data updates
     * @param gamePk
     */
    videoClosed(gamePk) {

        delete this.reference.componentsPlayingVideo[gamePk];

    }

    /**
     * @function videoStarted
     * @description Video started, must pause data updates
     * @param gamePk
     */
    videoStarted(gamePk) {

        const { reference } = this;

        if (reference.components[gamePk] && reference.components[gamePk].hasInstance()) {

            reference.components[gamePk].emit('video-started');

        }

        reference.componentsPlayingVideo[gamePk] = true;

    }

    /**
     *
     * @function clear
     * @description cleans out component references
     *
     */
    clear() {

        const { core, opts, props, reference } = this;
        const { controls } = core;
        const { element } = props;
        const { mode } = opts;
        const { useControls } = ViewModelHelpers;

        props.hasComponents = false;

        if (mode === 'game-selector') {

            const { components } = reference;

            for (const gameKey in components) {

                if (Object.prototype.hasOwnProperty.call(components, gameKey)) {

                    const container = element.querySelector(`.g5-component--mlb-scores--${gameKey}`);
                    container.removeEventListener('click', this.gameSelectorEvent.bind(this, gameKey));
                    components[gameKey].viewModel.removeListener('pause', this.pauseComponentRendering.bind(this));
                    components[gameKey].viewModel.removeListener('resume', this.resumeComponentRendering.bind(this));

                }
            }
        }

        reference.components = {};
        reference.pausedComponents = {};
        reference.componentsPlayingVideo = {};

        if (useControls(opts)) {

            controls.clear();

        }

    }

    /**
     *
     * @method init
     * @description instantiates scores viewModel
     *
     */
    init(scores) {

        const { core, props, opts } = this;
        const { instance } = props;
        const { useControls } = ViewModelHelpers;

        util.log('mlbScores viewModel - init');

        if (!instance) {

            props.instance = true;
            props.element = opts.container;
            this.opts.element = opts.container;

            //
            // update base display modifiers
            //
            this.updateModifiers();

            //
            // attach controls instance to horizontal scores
            //
            if (useControls(opts)) {

                core.controls = new scoresControls(this.opts, scores);

            }

        }
    }

}

export default ScoresViewModel;
